import { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Search, ZoomIn, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useRole } from "@/lib/useRole";

export const RPT_STATUS_BG = {
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  "Content validation in progress": "bg-sky-50 text-sky-700 border-sky-200",
  "Under scientific review": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Revision required": "bg-amber-50 text-amber-700 border-amber-200",
  "Approved for reporting": "bg-teal-50 text-teal-700 border-teal-200",
  "Locked final": "bg-emerald-50 text-emerald-700 border-emerald-200",
  Superseded: "bg-slate-100 text-slate-400 border-slate-200",
  Archived: "bg-slate-100 text-slate-400 border-slate-200",
};
export const SEC_STATUS_BG = {
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  "Ready for review": "bg-sky-50 text-sky-700 border-sky-200",
  "Under review": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Revision required": "bg-amber-50 text-amber-700 border-amber-200",
  "Scientifically approved": "bg-teal-50 text-teal-700 border-teal-200",
  "Approved for reporting": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const ZOOMS = [["fit-width", "Fit width", 1], ["fit-page", "Fit page", 0.78], ["zoom-125", "125%", 1.25]];

function SectionReviewRail({ report, sec, onUpdated }) {
  const { canApprove } = useRole();
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState("");
  const [busy, setBusy] = useState(false);
  const locked = report.status === "Locked final";
  const act = (action) => {
    if (action === "request_revision" && !comment.trim()) {
      setCommentError("A reviewer comment is required when requesting a revision.");
      document.querySelector(`[data-testid="section-comment-${sec.id}"]`)?.focus();
      return;
    }
    setBusy(true);
    api.patch(`/reports/registry/${report.id}/sections/${sec.id}`, { action, comment: comment || null })
      .then((r) => { onUpdated(r.data); toast.success(`Section "${sec.title}" updated`); setComment(""); })
      .catch((e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Action failed — please retry.", { action: { label: "Retry", onClick: () => act(action) } }))
      .finally(() => setBusy(false));
  };
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-3" data-testid={`section-review-${sec.id}`} aria-busy={busy}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[9px] uppercase tracking-wide text-slate-400">Section review</span>
        <Badge variant="outline" className={`text-[9px] ${SEC_STATUS_BG[sec.status]}`}>{sec.status}</Badge>
        <span className="text-[10px] text-slate-500">Reviewer: {sec.reviewer || "Unassigned"} · Last reviewed: {sec.last_reviewed || "—"} · Citation check: {sec.citation_check} · Open issues: {sec.open_issues}</span>
        {busy && <span className="flex items-center gap-1 text-[10px] text-slate-400" role="status" aria-live="polite" data-testid={`section-review-loading-${sec.id}`}><Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />Saving…</span>}
      </div>
      {sec.comment && <p className="text-[10.5px] text-amber-700 mt-1.5">Reviewer comment: {sec.comment}</p>}
      {!locked && !canApprove && (
        <p className="text-[10px] text-slate-500 mt-1.5" data-testid="section-role-note">Section approval requires a Scientific Reviewer, Senior Reviewer or Administrator role — the server rejects unauthorized requests.</p>
      )}
      {!locked && canApprove && (
        <div className="mt-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <Input placeholder="Reviewer comment (required for revision)" value={comment} onChange={(e) => { setComment(e.target.value); if (commentError) setCommentError(""); }}
              aria-label={`Reviewer comment for section ${sec.title}`} aria-invalid={!!commentError} aria-describedby={commentError ? `section-comment-error-${sec.id}` : undefined}
              className={`h-7 text-[11px] flex-1 min-w-[200px] ${commentError ? "border-red-300" : ""}`} data-testid={`section-comment-${sec.id}`} />
            <Button variant="outline" className="h-7 text-[10.5px]" disabled={busy} onClick={() => act("approve")} data-testid={`section-approve-${sec.id}`} aria-label={`Approve section ${sec.title}`}>Approve section</Button>
            <Button variant="outline" className="h-7 text-[10.5px]" disabled={busy} onClick={() => act("approve_reporting")} aria-label={`Approve section ${sec.title} for reporting`}>Approve for reporting</Button>
            <Button variant="outline" className="h-7 text-[10.5px] text-amber-700 border-amber-300" disabled={busy} onClick={() => act("request_revision")} data-testid={`section-revise-${sec.id}`} aria-label={`Request revision of section ${sec.title}`}>Request revision</Button>
          </div>
          {commentError && <p id={`section-comment-error-${sec.id}`} className="text-[10.5px] text-red-600 mt-1" role="alert" data-testid={`section-comment-error-${sec.id}`}>{commentError}</p>}
        </div>
      )}
      {locked && <p className="text-[10px] text-slate-400 mt-1.5">Locked final — changes require a new version.</p>}
    </div>
  );
}

export default function ReportPreview({ report, onUpdated }) {
  const [content, setContent] = useState(null);
  const [trace, setTrace] = useState(null);
  useEffect(() => { api.get("/claims/traceability").then((r) => setTrace(r.data)).catch(() => {}); }, []);
  const [page, setPage] = useState(0);
  const [zoom, setZoom] = useState("fit-width");
  const [q, setQ] = useState("");

  useEffect(() => {
    setPage(0);
    if (report?.content_key === "executive") api.get("/reports/executive").then((r) => setContent(r.data));
    else setContent({ sections: [] });
  }, [report?.id, report?.content_key]);

  const pages = useMemo(() => {
    if (!report) return [];
    const secPages = report.sections.filter((s) => s.id !== "cover").map((sec) => {
      const c = (content?.sections || []).find((x) => x.id === sec.id);
      return { kind: "section", sec, content: c };
    });
    return [{ kind: "cover" }, { kind: "toc" }, ...secPages, { kind: "references" }];
  }, [report, content]);

  if (!report) return <p className="text-sm text-slate-400 p-6">Select a report from the Report Library.</p>;
  const isApproved = ["Approved for reporting", "Locked final"].includes(report.status);
  const scale = ZOOMS.find(([id]) => id === zoom)[2];
  const matches = q.trim() ? pages.map((p, i) => ({ p, i })).filter(({ p }) => {
    const hay = p.kind === "section" ? `${p.sec.title} ${JSON.stringify(p.content || {})}` : p.kind;
    return hay.toLowerCase().includes(q.trim().toLowerCase());
  }).map(({ i }) => i) : [];
  const cur = pages[page];

  return (
    <div className="flex gap-4" data-testid="report-preview-workspace">
      <div className="hidden lg:block w-56 shrink-0">
        <div className="sticky top-4 bg-white border border-slate-200/80 rounded-xl p-3 shadow-sm max-h-[75vh] overflow-y-auto" data-testid="report-outline" role="navigation" aria-label="Report outline">
          <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Report outline</p>
          {pages.map((p, i) => (
            <button key={i} onClick={() => setPage(i)}
              aria-current={i === page ? "page" : undefined}
              className={`w-full text-left text-[11px] rounded-md px-2 py-1 mb-0.5 ${i === page ? "bg-slate-900 text-white" : matches.includes(i) ? "bg-amber-50 text-amber-800" : "text-slate-600 hover:bg-slate-50"}`}
              data-testid={`outline-page-${i}`}>
              <span className="font-mono text-[9px] mr-1.5 opacity-60">{String(i + 1).padStart(2, "0")}</span>
              {p.kind === "cover" ? "Cover page" : p.kind === "toc" ? "Table of contents" : p.kind === "references" ? "Citation check" : p.sec.title}
              {p.kind === "section" && p.sec.status === "Approved for reporting" && <CheckCircle2 className="w-3 h-3 inline ml-1 text-teal-500" />}
              {p.kind === "section" && p.sec.status === "Revision required" && <AlertCircle className="w-3 h-3 inline ml-1 text-amber-500" />}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-w-0 space-y-3">
        {report.recall && (
          <div className={`border rounded-xl px-4 py-2.5 ${report.recall.status === "Recalled" ? "bg-red-50 border-red-300" : "bg-amber-50 border-amber-300"}`}
            role="alert" data-testid="report-recall-banner">
            <p className={`text-xs font-bold ${report.recall.status === "Recalled" ? "text-red-800" : "text-amber-800"}`}>
              This locked report is {report.recall.status.toUpperCase()} — correction cascade {report.recall.event_id} (source {report.recall.source_id} {report.recall.event}).
            </p>
            <p className="text-[10.5px] text-slate-600 mt-0.5">
              The locked version remains immutable for the audit record and is excluded from approved export.
              {report.recall.replacement_id && <> Replacement version under review: <span className="font-mono font-semibold text-teal-700" data-testid="recall-replacement-link">{report.recall.replacement_id}</span></>}
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="h-8 text-xs" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="page-prev" aria-label="Previous page"><ChevronLeft className="w-3.5 h-3.5" aria-hidden="true" />Prev</Button>
          <span className="text-[11px] text-slate-500 font-mono" data-testid="page-indicator" aria-live="polite">Page {page + 1} / {pages.length}</span>
          <Button variant="outline" className="h-8 text-xs" disabled={page >= pages.length - 1} onClick={() => setPage(page + 1)} data-testid="page-next" aria-label="Next page">Next<ChevronRight className="w-3.5 h-3.5" aria-hidden="true" /></Button>
          <Select value={zoom} onValueChange={setZoom}>
            <SelectTrigger className="h-8 w-28 text-[11px]" data-testid="zoom-select" aria-label="Zoom level"><ZoomIn className="w-3 h-3 mr-1" aria-hidden="true" /><SelectValue /></SelectTrigger>
            <SelectContent>{ZOOMS.map(([id, label]) => <SelectItem key={id} value={id} className="text-xs">{label}</SelectItem>)}</SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[160px] max-w-xs">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden="true" />
            <Input placeholder="Search within report…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 pl-8 text-xs" data-testid="report-search" aria-label="Search within report" />
          </div>
          {q.trim() && <span className="text-[10px] text-slate-400">{matches.length} matching page{matches.length !== 1 ? "s" : ""} highlighted in outline</span>}
        </div>

        <div className="overflow-auto">
          <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: scale < 1 ? `${100 / scale}%` : "100%" }}>
            <div className="relative bg-white border border-slate-200 rounded-lg shadow-md mx-auto max-w-3xl min-h-[70vh] flex flex-col" data-testid={`report-page-${page}`}>
              {!isApproved && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-10" data-testid="draft-watermark" aria-hidden="true">
                  <p className="text-2xl font-black text-red-500/15 -rotate-30 text-center leading-snug">DRAFT—NOT APPROVED<br />FOR EXTERNAL USE</p>
                </div>
              )}
              <div className="flex items-center justify-between px-8 pt-5 text-[9px] font-mono uppercase tracking-widest text-slate-400 border-b border-slate-100 pb-2">
                <span>{report.id} · v{report.version} · {report.status}</span>
                <span>{report.confidentiality}</span>
              </div>

              <div className="flex-1 px-8 py-6">
                {cur?.kind === "cover" && (
                  <div className="h-full flex flex-col justify-center py-16">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-teal-600">{report.type} · Illustrative Prototype</p>
                    <h2 className="font-heading text-2xl font-bold text-slate-900 mt-3 leading-snug">{report.title}</h2>
                    <p className="text-sm text-slate-500 mt-3">{report.purpose}</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-8 text-[11px] text-slate-600">
                      {[["Audience", report.audience], ["Geographic scope", report.scope], ["Evidence cut-off", report.evidence_cutoff],
                        ["Version", `v${report.version}`], ["Prepared by", report.owner], ["Reviewed by", report.reviewer],
                        ["Approver", report.approver], ["Generated", report.generated]].map(([l, v]) => (
                        <p key={l}><span className="font-mono text-[9px] uppercase text-slate-400 block">{l}</span>{v}</p>
                      ))}
                    </div>
                  </div>
                )}
                {cur?.kind === "toc" && (
                  <div>
                    <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">Table of contents</h3>
                    {pages.slice(2).map((p, i) => (
                      <button key={i} onClick={() => setPage(i + 2)} className="w-full flex items-baseline gap-2 text-left text-xs text-slate-600 py-1 hover:text-teal-700" data-testid={`toc-entry-${i}`}>
                        <span className="font-mono text-[10px] text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                        <span className="flex-1">{p.kind === "references" ? "Citation check" : p.sec.title}</span>
                        <span className="font-mono text-[10px] text-slate-400">p. {i + 3}</span>
                      </button>
                    ))}
                  </div>
                )}
                {cur?.kind === "section" && (
                  <div>
                    <h3 className="font-heading text-lg font-bold text-slate-900">{cur.sec.title}</h3>
                    {cur.content ? (
                      <div className="mt-3 space-y-2">
                        {(cur.content.paragraphs || []).map((p, i) => <p key={i} className="text-xs text-slate-600 leading-relaxed">{p}</p>)}
                        {(cur.content.bullets || []).length > 0 && (
                          <ul className="space-y-1.5">{cur.content.bullets.map((b, i) => (
                            <li key={i} className="text-xs text-slate-600 leading-snug flex gap-1.5"><span className="text-teal-500 shrink-0">•</span><span>{b}</span></li>))}</ul>
                        )}
                        {(cur.content.paragraphs2 || []).map((p, i) => <p key={i} className="text-xs font-semibold text-slate-700 mt-1">{p}</p>)}
                        {(cur.content.bullets2 || []).length > 0 && (
                          <ul className="space-y-1.5">{cur.content.bullets2.map((b, i) => (
                            <li key={i} className="text-xs text-slate-600 leading-snug flex gap-1.5"><span className="text-teal-500 shrink-0">•</span><span>{b}</span></li>))}</ul>
                        )}
                        {cur.sec.id === "recommendations" && (
                          <p className="text-[10px] text-slate-500 italic border-t border-slate-100 pt-2 mt-3">Recommendations are decision-support outputs and do not constitute autonomous clinical, regulatory or commercial decisions.</p>
                        )}
                        {cur.sec.id === "strategic_implications" && (
                          <p className="text-[10px] text-amber-700 border-t border-slate-100 pt-2 mt-3">Only implications with reporting approval enter approved versions; AI-generated drafts appear in watermarked drafts only and never as final expert conclusions.</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 mt-3 italic">Demonstration finding — section content composes from live platform intelligence when this report type is generated. Illustrative statistic—not for decision-making.</p>
                    )}
                  </div>
                )}
                {cur?.kind === "references" && (
                  <div data-testid="references-page">
                    <h3 className="font-heading text-lg font-bold text-slate-900">Citation check</h3>
                    {trace ? (
                      <div className="mt-3 space-y-4">
                        <div className={`border rounded-lg p-3 ${trace.citation_check.passed ? "border-teal-200 bg-teal-50/50" : "border-red-200 bg-red-50/50"}`} data-testid="citation-check-panel">
                          <p className={`text-xs font-bold ${trace.citation_check.passed ? "text-teal-800" : "text-red-700"}`}>
                            Citation check: {trace.citation_check.passed ? "PASSED" : "FAILED"}
                          </p>
                          {trace.citation_check.requirements.map((r) => (
                            <p key={r.name} className="text-[10.5px] text-slate-600 mt-1">
                              <span className={r.passed ? "text-teal-700 font-semibold" : "text-red-600 font-semibold"}>{r.passed ? "✓" : "✕"}</span> {r.name} — {r.detail}
                            </p>
                          ))}
                          {trace.citation_check.advisory && <p className="text-[10px] text-amber-700 mt-1.5">{trace.citation_check.advisory}</p>}
                          <p className="text-[10px] text-slate-500 mt-1.5">{trace.summary.traceable_claims} of {trace.summary.material_claims} material claims traceable · {trace.summary.total_passages} passages · {trace.summary.ineligible_passages} ineligible</p>
                        </div>
                        <p className="text-[10px] text-slate-400">The full numbered reference list appears in the References section of the report body.</p>
                      </div>
                    ) : <p className="text-xs text-slate-400 mt-3 italic" role="status" aria-live="polite">Loading traceability…</p>}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between px-8 pb-4 pt-2 text-[9px] font-mono uppercase tracking-widest text-slate-400 border-t border-slate-100">
                <span>Evidence cut-off {report.evidence_cutoff} · Prepared by {report.owner}</span>
                <span>Page {page + 1} of {pages.length}</span>
              </div>
            </div>
          </div>
        </div>

        {cur?.kind === "section" && <SectionReviewRail report={report} sec={cur.sec} onUpdated={onUpdated} />}
      </div>
    </div>
  );
}
